import { useState, useEffect, useMemo, ChangeEvent } from 'react'
import {
  Box,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import RefreshIcon from '@mui/icons-material/Refresh'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import SaveIcon from '@mui/icons-material/Save'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getStickers } from '@/data/stickers/getStickers'
import { getStickerById } from '@/data/stickers/getStickerById'
import { createSticker } from '@/data/stickers/createSticker'
import { queryKeys } from '@/lib/queryKeys'

interface StickerSettings {
  phrase: string
  rows: number
  columns: number
  textSize: number
  padding: number
  textColor: string
  bgColor: string
  rotation: number
  font: string
  wrapText: boolean
  showAboriginalFlag: boolean
  showPalestinianFlag: boolean
}

const STORAGE_KEY = 'slap-sticker-settings'

const DEFAULT_SETTINGS: StickerSettings = {
  phrase: 'HELLO',
  rows: 3,
  columns: 2,
  textSize: 32,
  padding: 10,
  textColor: '#ffffff',
  bgColor: '#000000',
  rotation: 180,
  font: 'League Spartan',
  wrapText: true,
  showAboriginalFlag: false,
  showPalestinianFlag: false,
}

const FLAG_SIZE = 20

function AboriginalFlag({ size = FLAG_SIZE }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 100 60">
      <rect x="0" y="0" width="100" height="30" fill="#000000" />
      <rect x="0" y="30" width="100" height="30" fill="#CC0000" />
      <circle cx="50" cy="30" r="14" fill="#FFCD00" />
    </svg>
  )
}

function PalestinianFlag({ size = FLAG_SIZE }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 100 60">
      <rect x="0" y="0" width="100" height="20" fill="#000000" />
      <rect x="0" y="20" width="100" height="20" fill="#FFFFFF" />
      <rect x="0" y="40" width="100" height="20" fill="#007A3D" />
      <polygon points="0,0 30,30 0,60" fill="#CE1126" />
    </svg>
  )
}

const FONTS = [
  { label: 'Atkinson Hyperlegible', value: 'Atkinson Hyperlegible' },
  { label: 'League Spartan', value: 'League Spartan' },
  { label: 'Anton', value: 'Anton' },
  { label: 'Bebas Neue', value: 'Bebas Neue' },
  { label: 'Black Ops One', value: 'Black Ops One' },
  { label: 'Bungee', value: 'Bungee' },
  { label: 'Creepster', value: 'Creepster' },
  { label: 'Fugaz One', value: 'Fugaz One' },
  { label: 'Impact (system)', value: 'Impact' },
  { label: 'Lilita One', value: 'Lilita One' },
  { label: 'Oswald', value: 'Oswald' },
  { label: 'Permanent Marker', value: 'Permanent Marker' },
  { label: 'Pirata One', value: 'Pirata One' },
  { label: 'Protest Guerrilla', value: 'Protest Guerrilla' },
  { label: 'Rubik Mono One', value: 'Rubik Mono One' },
  { label: 'Special Elite', value: 'Special Elite' },
  { label: 'Stalinist One', value: 'Stalinist One' },
  { label: 'Ultra', value: 'Ultra' },
]

const PRESETS = [
  { label: '2×3', rows: 2, columns: 3 },
  { label: '3×2', rows: 3, columns: 2 },
  { label: '4×2', rows: 4, columns: 2 },
  { label: '3×3', rows: 3, columns: 3 },
]

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Anton&family=Atkinson+Hyperlegible:wght@400;700&family=Bebas+Neue&family=Black+Ops+One&family=Bungee&family=Creepster&family=Fugaz+One&family=League+Spartan:wght@700;900&family=Lilita+One&family=Oswald:wght@700&family=Permanent+Marker&family=Pirata+One&family=Protest+Guerrilla&family=Rubik+Mono+One&family=Special+Elite&family=Stalinist+One&family=Ultra&display=swap');
  
  @media print {
    body * { visibility: hidden; }
    .print-area, .print-area * { visibility: visible; }
    .print-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
    }
    .a4-page {
      width: 210mm;
      height: 297mm;
      margin: 0 !important;
      box-shadow: none !important;
      page-break-after: always;
    }
    .vertical-text {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .no-print { display: none !important; }
    @page {
      size: A4;
      margin: 0;
    }
  }

  .a4-page {
    width: 210mm;
    height: 297mm;
    background: white;
    margin: 20px auto;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    display: grid;
    gap: 5mm;
    padding: 10mm;
    box-sizing: border-box;
  }

  .sticker-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
  }

  .vertical-text {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    font-weight: 900;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    user-select: none;
    line-height: 1;
    max-height: 100%;
    text-align: center;
  }
`

export function SlapStickerMaker() {
  const queryClient = useQueryClient()
  const [settings, setSettings] = useState<StickerSettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved
      ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) }
      : DEFAULT_SETTINGS
  })
  const [stickerName, setStickerName] = useState('')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [selectedStickerId, setSelectedStickerId] = useState('')
  const [pageRotation, setPageRotation] = useState(0)

  // Fetch list of saved stickers
  const { data: savedStickers = [] } = useQuery({
    queryKey: queryKeys.stickers.lists(),
    queryFn: () => getStickers(),
  })

  // Save sticker mutation
  const saveMutation = useMutation({
    mutationFn: createSticker,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stickers.lists() })
      setSaveDialogOpen(false)
      setStickerName('')
    },
  })

  // Load sticker
  const loadSticker = async (stickerId: string) => {
    if (!stickerId) return
    const sticker = await getStickerById({ data: { id: stickerId } })
    setSettings({
      phrase: sticker.phrase,
      rows: sticker.rows,
      columns: sticker.columns,
      textSize: sticker.textSize,
      padding: sticker.padding,
      textColor: sticker.textColor,
      bgColor: sticker.bgColor,
      rotation: DEFAULT_SETTINGS.rotation,
      font: sticker.fontFamily ?? DEFAULT_SETTINGS.font,
      wrapText: sticker.wrapText ?? DEFAULT_SETTINGS.wrapText,
      showAboriginalFlag: DEFAULT_SETTINGS.showAboriginalFlag,
      showPalestinianFlag: DEFAULT_SETTINGS.showPalestinianFlag,
    })
  }

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  }, [settings])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault()
        window.print()
      }
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  const updateSetting = <TKey extends keyof StickerSettings>(
    key: TKey,
    value: StickerSettings[TKey],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleNumberChange =
    (key: keyof StickerSettings, min: number, max: number) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = Math.max(
        min,
        Math.min(max, parseInt(e.target.value) || min),
      )
      updateSetting(key, value as any)
    }

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS)
  }

  const applyPreset = (rows: number, columns: number) => {
    setSettings((prev) => ({ ...prev, rows, columns }))
  }

  const handleSave = () => {
    if (!stickerName.trim()) return
    saveMutation.mutate({
      data: {
        name: stickerName,
        phrase: settings.phrase,
        rows: settings.rows,
        columns: settings.columns,
        textSize: settings.textSize,
        padding: settings.padding,
        textColor: settings.textColor,
        bgColor: settings.bgColor,
        fontFamily: settings.font,
        wrapText: settings.wrapText,
      },
    })
  }

  const handleLoadChange = (stickerId: string) => {
    setSelectedStickerId(stickerId)
    loadSticker(stickerId)
  }

  const stickers = useMemo(
    () => Array.from({ length: settings.rows * settings.columns }),
    [settings.rows, settings.columns],
  )

  return (
    <>
      <style>{STYLES}</style>

      <Box className="no-print">
        <AppBar position="sticky" elevation={1}>
          <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap', py: 1, minHeight: 64 }}>
            <Typography
              variant="h6"
              sx={{ mr: 2, flexShrink: 0, fontWeight: 700 }}
            >
              Slap Stickers
            </Typography>

            <TextField
              label="Text"
              value={settings.phrase}
              onChange={(e) => updateSetting('phrase', e.target.value)}
              size="small"
              placeholder="Enter text"
              sx={{ minWidth: 180, bgcolor: 'white', borderRadius: 1 }}
            />

            <FormControl
              size="small"
              sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}
            >
              <InputLabel>Font</InputLabel>
              <Select
                value={settings.font}
                label="Font"
                onChange={(e) => updateSetting('font', e.target.value)}
              >
                {FONTS.map((f) => (
                  <MenuItem
                    key={f.value}
                    value={f.value}
                    sx={{ fontFamily: `'${f.value}', sans-serif` }}
                  >
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  variant={
                    settings.rows === preset.rows &&
                    settings.columns === preset.columns
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={() => applyPreset(preset.rows, preset.columns)}
                  sx={{ minWidth: 45, bgcolor: 'white' }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>

            <TextField
              label="Rows"
              type="number"
              inputProps={{ min: 1, max: 10 }}
              value={settings.rows}
              onChange={handleNumberChange('rows', 1, 10)}
              size="small"
              sx={{ width: 70, bgcolor: 'white', borderRadius: 1 }}
            />

            <TextField
              label="Cols"
              type="number"
              inputProps={{ min: 1, max: 10 }}
              value={settings.columns}
              onChange={handleNumberChange('columns', 1, 10)}
              size="small"
              sx={{ width: 70, bgcolor: 'white', borderRadius: 1 }}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{ bgcolor: 'white', opacity: 0.3 }}
            />

            <TextField
              label="Size"
              type="number"
              inputProps={{ min: 12, max: 150 }}
              value={settings.textSize}
              onChange={handleNumberChange('textSize', 12, 150)}
              size="small"
              sx={{ width: 70, bgcolor: 'white', borderRadius: 1 }}
            />

            <TextField
              label="Padding"
              type="number"
              inputProps={{ min: 0, max: 100 }}
              value={settings.padding}
              onChange={handleNumberChange('padding', 0, 100)}
              size="small"
              sx={{ width: 80, bgcolor: 'white', borderRadius: 1 }}
            />

            <TextField
              label="Text"
              type="color"
              value={settings.textColor}
              onChange={(e) => updateSetting('textColor', e.target.value)}
              size="small"
              sx={{ width: 70 }}
            />

            <TextField
              label="BG"
              type="color"
              value={settings.bgColor}
              onChange={(e) => updateSetting('bgColor', e.target.value)}
              size="small"
              sx={{ width: 70 }}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{ bgcolor: 'white', opacity: 0.3 }}
            />

            <Tooltip title="Toggle Aboriginal flag">
              <Button
                size="small"
                variant={settings.showAboriginalFlag ? 'contained' : 'outlined'}
                onClick={() =>
                  updateSetting(
                    'showAboriginalFlag',
                    !settings.showAboriginalFlag,
                  )
                }
                sx={{
                  minWidth: 40,
                  bgcolor: settings.showAboriginalFlag ? undefined : 'white',
                }}
              >
                <AboriginalFlag size={24} />
              </Button>
            </Tooltip>

            <Tooltip title="Toggle Palestinian flag">
              <Button
                size="small"
                variant={
                  settings.showPalestinianFlag ? 'contained' : 'outlined'
                }
                onClick={() =>
                  updateSetting(
                    'showPalestinianFlag',
                    !settings.showPalestinianFlag,
                  )
                }
                sx={{
                  minWidth: 40,
                  bgcolor: settings.showPalestinianFlag ? undefined : 'white',
                }}
              >
                <PalestinianFlag size={24} />
              </Button>
            </Tooltip>

            <Tooltip title="Toggle text wrapping">
              <Button
                size="small"
                variant={settings.wrapText ? 'contained' : 'outlined'}
                onClick={() => updateSetting('wrapText', !settings.wrapText)}
                sx={{
                  minWidth: 45,
                  bgcolor: settings.wrapText ? undefined : 'white',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Wrap
              </Button>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            <FormControl
              size="small"
              sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}
            >
              <InputLabel>Load Saved</InputLabel>
              <Select
                value={selectedStickerId}
                label="Load Saved"
                onChange={(e) => handleLoadChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>New Sticker</em>
                </MenuItem>
                {savedStickers.map((sticker) => (
                  <MenuItem key={sticker.id} value={sticker.id}>
                    {sticker.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Save
            </Button>

            <Tooltip title="Rotate page preview">
              <IconButton
                onClick={() => setPageRotation((r) => (r + 90) % 360)}
                sx={{ color: 'white' }}
              >
                <RotateRightIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reset to defaults">
              <IconButton onClick={resetSettings} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Toolbar>
        </AppBar>
      </Box>

      <Container
        maxWidth={false}
        sx={{ py: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}
      >
        <div className="print-area">
          <div
            className="a4-page"
            style={{
              gridTemplateRows: `repeat(${settings.rows}, 1fr)`,
              gridTemplateColumns: `repeat(${settings.columns}, 1fr)`,
              transform: pageRotation
                ? `rotate(${pageRotation}deg)`
                : undefined,
              transition: 'transform 0.3s ease',
            }}
          >
            {stickers.map((_, index) => (
              <div key={index} className="sticker-cell">
                <div
                  className="vertical-text"
                  style={{
                    fontSize: `${settings.textSize}px`,
                    fontFamily: `'${settings.font}', sans-serif`,
                    color: settings.textColor,
                    backgroundColor: settings.bgColor,
                    padding: `${settings.padding}px`,
                    margin: `-${settings.padding}px`,
                    transform: `rotate(${settings.rotation}deg)`,
                    whiteSpace: settings.wrapText ? 'normal' : 'nowrap',
                  }}
                >
                  {settings.phrase}
                </div>
                {settings.showAboriginalFlag && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      left: 4,
                      transform: 'rotate(-90deg)',
                    }}
                  >
                    <AboriginalFlag size={28} />
                  </div>
                )}
                {settings.showPalestinianFlag && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      transform: 'rotate(-90deg)',
                    }}
                  >
                    <PalestinianFlag size={28} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Sticker</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sticker Name"
            fullWidth
            value={stickerName}
            onChange={(e) => setStickerName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSave()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!stickerName.trim() || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
